/**
 * FRONT-2594 — pure layout decisions for the chat window's Canvas panel.
 *
 * The chat's existing children are NEVER wrapped or re-parented (that would
 * remount the composer and the live stream). The layout is CSS keyed on a class
 * on the chat card, applied only while the panel is on screen. With the flag off
 * the card and header markup is exactly main's. With Canvas on but the panel
 * closed, the only differences are the header's Canvas control (wide: a button;
 * narrow: the switch, plus below `sm` its own row under the header) and the
 * Close tooltip reading "Close" while a Canvas draft is dirty.
 */

export type CanvasLayout =
  /** Flag off, or the chat has no items: no Canvas UI at all. */
  | 'off'
  /** Wide screen, panel closed: header button only, layout unchanged. */
  | 'closed'
  /** Wide screen, panel open: chat column + panel side by side. */
  | 'wide'
  /** Narrow screen, Chat view showing (default). */
  | 'narrow-chat'
  /** Narrow screen, Canvas view showing; the chat column is hidden with CSS. */
  | 'narrow-canvas';

export function canvasLayout(input: {
  enabled: boolean;
  itemCount: number;
  open: boolean;
  wide: boolean;
}): CanvasLayout {
  if (!input.enabled || input.itemCount === 0) return 'off';
  if (input.wide) return input.open ? 'wide' : 'closed';
  return input.open ? 'narrow-canvas' : 'narrow-chat';
}

/** True when the panel itself is rendered. */
export function isPanelVisible(layout: CanvasLayout): boolean {
  return layout === 'wide' || layout === 'narrow-canvas';
}

/** Class added to the chat card while the panel is on screen; null otherwise. */
export function canvasCardClass(layout: CanvasLayout): string | null {
  if (layout === 'wide') return 'eddy-canvas-wide';
  if (layout === 'narrow-canvas') return 'eddy-canvas-narrow';
  return null;
}

/** An open panel needs the room: ignore the reading-width pref while it is open. */
export function canvasCardMaxWidth<W extends number | 'none'>(layout: CanvasLayout, prefMax: W, fullMax: W): W {
  return layout === 'wide' ? fullMax : prefMax;
}

/**
 * The two rules. The card's first child is the 60px header (kept full width);
 * every other child except the panel (and this style tag) is the chat.
 * Wide: chat column ~44% (min 400px) at the left, panel fills the rest.
 * Narrow: chat children are `display: none` — hidden, never unmounted, so the
 * scroll position and the composer draft survive a trip to the Canvas view.
 */
export const CANVAS_LAYOUT_CSS = `
  .eddy-canvas-wide, .eddy-canvas-narrow { position: relative; }
  .eddy-canvas-wide > :not(:first-child):not(style):not([data-canvas-region]) {
    width: max(44%, 400px); flex-shrink: 0;
  }
  .eddy-canvas-wide > [data-canvas-region="panel"] {
    position: absolute; top: 60px; bottom: 0; right: 0; left: max(44%, 400px);
  }
  .eddy-canvas-narrow > :not(:first-child):not(style):not([data-canvas-region]) { display: none; }
  .eddy-canvas-narrow > [data-canvas-region="panel"] {
    position: absolute; top: 60px; bottom: 0; right: 0; left: 0;
  }
  /* Below sm the Chat | Canvas switch is its own 44px row under the 60px header. */
  @media (max-width: 639px) {
    .eddy-canvas-narrow > [data-canvas-region="panel"] { top: 104px; }
  }
`;
